// client/src/components/pages/LexiStar/Replay/ReplayCharts.tsx
//
// Synchronised replay charts (FR-RPL-002; LS-09 §8 "one charting library …
// shared cursor with map"): speed with the posted limit, and fuel level (%)
// with litres used. Both share the replay clock: the playhead is an overlay
// line on the plot area (so the charts themselves do not re-render every
// frame), recharts `syncId` keeps their tooltips together, and a click on
// either chart seeks the replay there. Each chart states its gist in text.
//
// ST-00243: series, grid, ticks and the tooltip resolve through the theme's
// colour variables (the speed line was slate-700 hex — invisible on the dark
// surface); multi-day axes label midnight with the date. The axes and lines are
// passed as arrays: inside a fragment recharts 2 (then on react-is 18) dropped
// them under React 19, so neither chart drew a line. ST-00260 pinned recharts'
// react-is to 19 (package.json override), so fragments work again; the arrays stay.
// Converted from ReplayCharts.jsx in the same change (CLAUDE.md TypeScript rule).

import { memo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import type { CategoricalChartState } from 'recharts/types/chart/types';
import { clockTime, timeTicks, tickLabel, speedSummary, fuelSummary } from './replayLogic';
import type { FuelRow, SpeedRow } from '../../../../types/lexistar-replay';

const MARGIN = { top: 8, right: 12, bottom: 0, left: 4 };
const Y_WIDTH = 40;
const X_HEIGHT = 22;
const HEIGHT = 150;

// Theme tokens (src/index.css): each flips with data-theme and keeps ≥ 3:1
// against the card surface in light and dark.
const tw = (name: string) => `rgb(var(--tw-${name}))`;
const SPEED = tw('slate-700');
const LIMIT = tw('amber-700');
const LEVEL = tw('sky-700');
const USED = tw('violet-700');
const GRID = tw('slate-200');
const TICK = { fontSize: 10, fill: tw('slate-500') };
const TOOLTIP_STYLE = {
  background: 'var(--surface-raised)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  color: 'var(--text)',
  fontSize: 12,
};
const TOOLTIP_LABEL = { color: 'var(--text-strong)', fontWeight: 600 };

type Kind = 'speed' | 'fuel';

interface ChartBodyProps {
  rows: (SpeedRow | FuelRow)[];
  start: number;
  end: number;
  kind: Kind;
  onSeek?: (t: number) => void;
}

const num = (v: unknown) => (v === null || v === undefined ? null : Number(v));

const ChartBody = memo(function ChartBody({ rows, start, end, kind, onSeek }: ChartBodyProps) {
  const ticks = timeTicks(start, end, 6);
  const click = (state: CategoricalChartState | null) => {
    const t = Number(state?.activeLabel);
    if (Number.isFinite(t)) onSeek?.(t);
  };
  return (
    <ResponsiveContainer width="100%" height={HEIGHT}>
      <LineChart data={rows} margin={MARGIN} syncId="lexistar-replay" onClick={click}>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="t"
          type="number"
          domain={[start, end]}
          ticks={ticks}
          tickFormatter={(t: number) => tickLabel(t)}
          height={X_HEIGHT}
          tick={TICK}
          stroke={GRID}
          allowDataOverflow
        />
        {/* Arrays, not fragments: recharts 2 finds its children with react-is, and
            the react-is 18 it shipped with did not recognise a React 19 fragment, so
            a fragment's axes and lines were silently dropped (the charts drew
            nothing). Fixed repo-wide by the react-is 19 override (ST-00260). */}
        {kind === 'speed'
          ? [
              <YAxis
                key="y"
                width={Y_WIDTH}
                tick={TICK}
                stroke={GRID}
                unit=""
                domain={[0, 'auto']}
              />,
              <Tooltip
                key="tooltip"
                contentStyle={TOOLTIP_STYLE}
                labelStyle={TOOLTIP_LABEL}
                labelFormatter={(t) => clockTime(Number(t))}
                formatter={(v, name) => [
                  num(v) === null ? '—' : `${Math.round(Number(v))} km/h`,
                  name,
                ]}
                isAnimationActive={false}
              />,
              <Line
                key="kmh"
                type="monotone"
                dataKey="kmh"
                name="Speed"
                stroke={SPEED}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />,
              <Line
                key="limit"
                type="stepAfter"
                dataKey="limit"
                name="Limit"
                stroke={LIMIT}
                strokeWidth={1.5}
                strokeDasharray="5 3"
                dot={false}
                isAnimationActive={false}
                connectNulls
              />,
            ]
          : [
              <YAxis
                key="y-level"
                yAxisId="level"
                width={Y_WIDTH}
                tick={TICK}
                stroke={GRID}
                domain={[0, 100]}
              />,
              <YAxis
                key="y-used"
                yAxisId="used"
                orientation="right"
                width={Y_WIDTH}
                tick={TICK}
                stroke={GRID}
                domain={[0, 'auto']}
              />,
              <Tooltip
                key="tooltip"
                contentStyle={TOOLTIP_STYLE}
                labelStyle={TOOLTIP_LABEL}
                labelFormatter={(t) => clockTime(Number(t))}
                formatter={(v, name) => [
                  num(v) === null
                    ? '—'
                    : name === 'Level'
                      ? `${Math.round(Number(v))}%`
                      : `${Math.round(Number(v))} L`,
                  name,
                ]}
                isAnimationActive={false}
              />,
              <Line
                key="level"
                yAxisId="level"
                type="monotone"
                dataKey="level_pct"
                name="Level"
                stroke={LEVEL}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />,
              <Line
                key="used"
                yAxisId="used"
                type="monotone"
                dataKey="used_l"
                name="Used"
                stroke={USED}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />,
            ]}
      </LineChart>
    </ResponsiveContainer>
  );
});

function Playhead({
  cursor,
  start,
  end,
  rightAxis,
}: {
  cursor: number;
  start: number;
  end: number;
  rightAxis: boolean;
}) {
  const f = Math.max(0, Math.min(1, (cursor - start) / (end - start)));
  return (
    <div
      className="pointer-events-none absolute"
      style={{
        top: MARGIN.top,
        bottom: X_HEIGHT + MARGIN.bottom,
        left: MARGIN.left + Y_WIDTH,
        right: MARGIN.right + (rightAxis ? Y_WIDTH : 0),
      }}
      aria-hidden="true"
    >
      <div className="absolute top-0 bottom-0 w-px bg-slate-900" style={{ left: `${f * 100}%` }} />
    </div>
  );
}

type LegendItem = [label: string, color: string, dashed?: boolean];

function Legend({ items }: { items: LegendItem[] }) {
  return (
    <ul className="flex flex-wrap gap-x-3 text-[11px] text-slate-600">
      {items.map(([label, color, dashed]) => (
        <li key={label} className="inline-flex items-center gap-1">
          <span
            className="inline-block w-4"
            style={dashed ? { borderTop: `2px dashed ${color}` } : { height: 2, background: color }}
          />
          {label}
        </li>
      ))}
    </ul>
  );
}

export interface ReplayChartsProps {
  speed: SpeedRow[];
  fuel: FuelRow[];
  start: number;
  end: number;
  cursor: number;
  onSeek?: (t: number) => void;
}

export default function ReplayCharts({
  speed,
  fuel,
  start,
  end,
  cursor,
  onSeek,
}: ReplayChartsProps) {
  const block = (
    title: string,
    kind: Kind,
    rows: (SpeedRow | FuelRow)[],
    summary: string,
    legend: LegendItem[]
  ) => (
    <figure className="min-w-0" aria-label={title}>
      <figcaption className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {title}
        </span>
        <Legend items={legend} />
      </figcaption>
      <p className="text-xs text-slate-600 mb-1" data-testid={`${kind}-summary`}>
        {summary}
      </p>
      {rows.length > 0 ? (
        <div className="relative" style={{ height: HEIGHT }}>
          <ChartBody rows={rows} start={start} end={end} kind={kind} onSeek={onSeek} />
          <Playhead cursor={cursor} start={start} end={end} rightAxis={kind === 'fuel'} />
        </div>
      ) : (
        <p className="text-xs text-slate-500 italic py-6 text-center border border-dashed border-slate-300 rounded-lg">
          No {kind} samples in this window.
        </p>
      )}
    </figure>
  );
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {block('Speed', 'speed', speed, speedSummary(speed), [
        ['Speed km/h', SPEED],
        ['Limit', LIMIT, true],
      ])}
      {block('Fuel', 'fuel', fuel, fuelSummary(fuel), [
        ['Level %', LEVEL],
        ['Used L', USED],
      ])}
    </div>
  );
}
